import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { FileText, Check, XCircle, Clock, Download, Eye, ChevronDown, ChevronUp, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
};

const STATUS_ICONS = { pending: Clock, approved: Check, rejected: XCircle };

const COUNTRY_NAMES = {
  IT: 'Italia', PL: 'Polonia', DE: 'Germania', FR: 'Francia', ES: 'Spagna',
  GB: 'Regno Unito', US: 'Stati Uniti', AT: 'Austria', CH: 'Svizzera',
  NL: 'Paesi Bassi', RO: 'Romania', CZ: 'Rep. Ceca',
};

const DOC_LABELS = {
  id_document: 'Documento identità',
  fiscal_code: 'Codice Fiscale',
  proof_of_address: 'Prova indirizzo',
  business_certificate: 'Certificato azienda',
};

export default function ComplianceReview() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expanded, setExpanded] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewing, setReviewing] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/compliance/submissions?status=${filter}`);
      setSubmissions(res.data);
    } catch (err) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleReview = async (id, action) => {
    if (action === 'reject' && !rejectionReason.trim()) {
      toast.error('Inserisci il motivo del rifiuto');
      return;
    }
    setReviewing(id);
    try {
      const res = await api.post(`/compliance/review/${id}`, {
        action,
        rejection_reason: action === 'reject' ? rejectionReason : '',
      });
      toast.success(res.data.message);
      setRejectionReason('');
      setExpanded(null);
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setReviewing(null); }
  };

  const handleDownload = async (storagePath, filename) => {
    try {
      const res = await api.get(`/compliance/files/${storagePath}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore download file');
    }
  };

  const filters = [
    { value: 'pending', label: 'In attesa', count: null },
    { value: 'approved', label: 'Approvati', count: null },
    { value: 'rejected', label: 'Rifiutati', count: null },
    { value: 'all', label: 'Tutti', count: null },
  ];

  return (
    <Layout>
      <PageHeader title="Verifica Documenti" subtitle="Revisione documenti compliance per acquisto numeri" />
      <div className="content-area max-w-5xl" data-testid="compliance-review-page">

        {/* Filters */}
        <div className="flex gap-2 mb-6">
          {filters.map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                filter === f.value ? 'bg-sky-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-sky-300'
              }`} data-testid={`filter-${f.value}`}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
        ) : submissions.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nessuna richiesta {filter !== 'all' ? `con stato "${filter}"` : ''}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {submissions.map((s) => {
              const Icon = STATUS_ICONS[s.status] || Clock;
              const isExpanded = expanded === s.id;
              return (
                <div key={s.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`submission-${s.id}`}>
                  {/* Header */}
                  <div className="p-4 cursor-pointer flex items-center gap-4" onClick={() => setExpanded(isExpanded ? null : s.id)}>
                    <div className={`w-3 h-3 rounded-full shrink-0 ${s.status === 'pending' ? 'bg-amber-500 animate-pulse' : s.status === 'approved' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-800">{s.partner_name || s.partner_email}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_COLORS[s.status]}`}>
                          <Icon className="w-3 h-3 inline mr-1" />{s.status === 'pending' ? 'In attesa' : s.status === 'approved' ? 'Approvato' : 'Rifiutato'}
                        </span>
                        <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">{COUNTRY_NAMES[s.country_code] || s.country_code}</span>
                        <span className="px-2 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">{s.requester_type === 'business' ? 'Azienda' : 'Privato'}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                        <span>Intestatario: {s.requester_name}</span>
                        {s.requester_fiscal_code && <span>CF/P.IVA: {s.requester_fiscal_code}</span>}
                        <span>{new Date(s.created_at).toLocaleDateString('it-IT')}</span>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">{s.documents?.length || 0} doc</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>

                  {/* Expanded */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/70 p-5 space-y-4">
                      {/* Documents */}
                      <div>
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Documenti caricati</h4>
                        <div className="space-y-2">
                          {s.documents?.map((doc, i) => (
                            <div key={i} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-200">
                              <FileText className="w-4 h-4 text-sky-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-700">{doc.label || DOC_LABELS[doc.doc_type] || doc.doc_type}</p>
                                <p className="text-xs text-slate-400 truncate">{doc.original_filename}</p>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); handleDownload(doc.storage_path, doc.original_filename); }}
                                className="p-2 hover:bg-sky-50 rounded-lg transition" data-testid={`download-${doc.doc_type}`}>
                                <Download className="w-4 h-4 text-sky-500" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Proof of address date */}
                      {s.proof_of_address_date && (
                        <div className="text-sm text-slate-600">
                          <span className="text-slate-400">Data prova indirizzo:</span>{' '}
                          <span className="font-medium">{new Date(s.proof_of_address_date).toLocaleDateString('it-IT')}</span>
                          {(() => {
                            const days = Math.floor((new Date() - new Date(s.proof_of_address_date)) / (1000*60*60*24));
                            return days > 90
                              ? <span className="text-red-500 ml-2 text-xs font-medium">SCADUTA ({days} giorni)</span>
                              : <span className="text-slate-400 ml-2 text-xs">({days} giorni fa)</span>;
                          })()}
                        </div>
                      )}

                      {/* Rejection reason if rejected */}
                      {s.status === 'rejected' && s.rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                          <p className="text-xs text-red-500 font-medium mb-1">Motivo rifiuto:</p>
                          <p className="text-sm text-red-700">{s.rejection_reason}</p>
                        </div>
                      )}

                      {/* Review Actions (only for pending) */}
                      {s.status === 'pending' && (
                        <div className="flex flex-col gap-3 pt-2">
                          <div className="flex gap-3">
                            <Button onClick={() => handleReview(s.id, 'approve')} disabled={reviewing === s.id}
                              className="bg-emerald-500 hover:bg-emerald-600 flex-1" data-testid={`approve-${s.id}`}>
                              {reviewing === s.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                              Approva
                            </Button>
                            <Button variant="outline" onClick={() => handleReview(s.id, 'reject')} disabled={reviewing === s.id}
                              className="border-red-300 text-red-600 hover:bg-red-50 flex-1" data-testid={`reject-${s.id}`}>
                              <XCircle className="w-4 h-4 mr-2" /> Rifiuta
                            </Button>
                          </div>
                          <input value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}
                            placeholder="Motivo rifiuto (obbligatorio per rifiutare)"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                            data-testid={`rejection-reason-${s.id}`} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
